/**
 * ENGAGE-PRICING-SPLIT-1 — split out of EngagementCreate.tsx to help bring it
 * back under the FILE_SIZE_POLICY 400-line hard stop after the server-quote
 * rewrite added lines back. Pure presentational: all pricing/tier state
 * still lives in EngagementCreate.tsx and is passed in as props.
 *
 * Add-on dollar amounts are correct; only their *location* (client-computed
 * instead of server-priced) is the open gap — tracked separately as
 * ENGAGE-ADDON-PRICING-1. See lib/engagementIntake.ts's header for the full
 * rationale on why that widening was rejected for this pass.
 *
 * ENGAGE-ADDON-PRICING-1 step 2 (2026-08-23): per-add-on price override,
 * mirroring TierFeeCard's tier-override block exactly — super_admin only, a
 * reason is required, and it will be logged to the audit trail (enforced
 * server-side in crud.py, not just here).
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PRICING_ENABLED } from "@/lib/pricing";
import { TIER_LABELS, type ScopeTier } from "@/lib/qoeCatalogue.generated";
import { ADD_ONS, addOnBaseAmount, fmt, tierAtLeast } from "@/lib/engagementIntake";

interface AddOnsCardProps {
  effectiveTier: ScopeTier;
  selectedAddOns: Set<string>;
  toggleAddOn: (key: string) => void;
  baseFee: number;
  isSuperAdmin: boolean;
  addOnOverrideAmounts: Record<string, string>;
  setAddOnOverrideAmount: (key: string, value: string) => void;
  addOnOverrideReasons: Record<string, string>;
  setAddOnOverrideReason: (key: string, value: string) => void;
}

export default function AddOnsCard({
  effectiveTier, selectedAddOns, toggleAddOn, baseFee, isSuperAdmin,
  addOnOverrideAmounts, setAddOnOverrideAmount,
  addOnOverrideReasons, setAddOnOverrideReason,
}: AddOnsCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{PRICING_ENABLED ? "Premium Add-ons" : "Add-ons"}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {ADD_ONS.map(ao => {
          const tierLocked  = !!ao.minTier && !tierAtLeast(effectiveTier, ao.minTier);
          const checked     = selectedAddOns.has(ao.key);
          const baseAmount  = addOnBaseAmount(ao, effectiveTier, baseFee);
          const overrideRaw = addOnOverrideAmounts[ao.key] ?? "";
          const overrideParsed = overrideRaw.trim() === "" ? null : parseFloat(overrideRaw);
          const isOverridden = overrideParsed !== null && !isNaN(overrideParsed) && overrideParsed !== baseAmount;
          const finalAmount  = isOverridden ? (overrideParsed as number) : baseAmount;
          const feeDisplay = ao.isPercent && !isOverridden
            ? `+${(ao.amount * 100).toFixed(0)}%`
            : `+${fmt(finalAmount)}`;

          return (
            <div
              key={ao.key}
              className={`rounded-lg border p-3 transition-colors
                ${tierLocked ? "opacity-40" : ""}
                ${checked && !tierLocked ? "border-[--color-primary] bg-[--color-primary]/5" : "border-gray-200"}
              `}
            >
              <label className={`flex items-center justify-between ${tierLocked ? "cursor-not-allowed" : "cursor-pointer hover:bg-gray-50"}`}>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={tierLocked}
                    onChange={() => !tierLocked && toggleAddOn(ao.key)}
                    className="h-4 w-4 accent-[--color-primary]"
                  />
                  <span className="text-sm text-gray-800">{ao.label}</span>
                  {tierLocked && ao.minTier && (
                    <span className="text-xs text-gray-400">({TIER_LABELS[ao.minTier]}+ only)</span>
                  )}
                </div>
                {PRICING_ENABLED && (
                  <span className="text-sm font-medium text-gray-700">{feeDisplay}</span>
                )}
              </label>

              {isSuperAdmin && checked && !tierLocked && (
                <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                  <div>
                    <Label htmlFor={`addon_override_${ao.key}`} className="text-xs">
                      Override price (base {fmt(baseAmount)})
                    </Label>
                    <Input
                      id={`addon_override_${ao.key}`}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={String(baseAmount)}
                      value={overrideRaw}
                      onChange={e => {
                        setAddOnOverrideAmount(ao.key, e.target.value);
                        setAddOnOverrideReason(ao.key, "");
                      }}
                    />
                  </div>
                  {isOverridden && (
                    <div>
                      <Label htmlFor={`addon_override_reason_${ao.key}`} className="text-xs">Override Reason *</Label>
                      <Input
                        id={`addon_override_reason_${ao.key}`}
                        placeholder="Reason for overriding this add-on's price…"
                        value={addOnOverrideReasons[ao.key] ?? ""}
                        onChange={e => setAddOnOverrideReason(ao.key, e.target.value)}
                        required
                      />
                      <p className="mt-1 text-xs text-amber-700">Override will be logged to audit trail.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
